// FILE: src/services/notifications.js
import {
  addDoc,
  collection,
  collectionGroup,
  deleteDoc,
  doc,
  limit,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  Timestamp,
  updateDoc,
  where,
} from "firebase/firestore";
import { getAuth } from "firebase/auth";
import { getFunctions, httpsCallable } from "firebase/functions";
import { db } from "@/firebase/firebase";
import { premiumizeCopy } from "@/utils/notificationCopy";

/**
 * Send a notification to a specific user.
 * - If you notify yourself, we write directly (allowed by rules).
 * - If you notify someone else, we call a Cloud Function (admin).
 */
export async function sendNotificationToUser({ userId, title, body = "", data = {} }) {
  const auth = getAuth();
  const callerUid = auth.currentUser?.uid;
  if (!callerUid) throw new Error("Must be signed in.");
  if (!userId) throw new Error("userId is required.");

  const cleanData = data || {};
  const copy = premiumizeCopy({ title, body, data: cleanData });

  if (userId === callerUid) {
    const ref = await addDoc(collection(db, `users/${userId}/notifications`), {
      kind: "user",
      userId,
      title: String(copy.title),
      body: String(copy.body),
      data: cleanData,
      isRead: false,
      createdAt: serverTimestamp(),
      timestamp: serverTimestamp(),
      createdBy: callerUid,
    });
    return ref.id;
  }

  // For other users, send to Cloud Function.
  // We still premiumize here so the server gets the improved copy too.
  const fn = httpsCallable(getFunctions(), "sendNotificationToUser");
  const res = await fn({ userId, title: copy.title, body: copy.body, data: cleanData });
  return (res?.data && res.data.id) || null;
}

/**
 * Subscribe to the caller's inbox from:
 *  1) /users/{uid}/notifications/*
 *  2) collection-group docs with kind=="user" and userId==uid
 *  3) (optional) collection-group team docs for teams the caller coaches
 */
export function subscribeToInbox({ onChange, teamIds = [], pageSize = 50 }) {
  const uid = getAuth().currentUser?.uid;
  if (!uid) throw new Error("Must be signed in.");

  const unsubs = [];
  const bag = new Map(); // path -> doc

  const push = (docSnap) => {
    const id = docSnap.ref.path;
    const data = docSnap.data();
    bag.set(id, {
      id,
      ...data,
      _ts:
        (data?.timestamp instanceof Timestamp && data.timestamp.toMillis()) ||
        (data?.createdAt instanceof Timestamp && data.createdAt.toMillis()) ||
        0,
    });
  };

  const pop = (docSnap) => bag.delete(docSnap.ref.path);

  const emit = () => {
    const list = Array.from(bag.values())
      .sort((a, b) => b._ts - a._ts)
      .slice(0, pageSize);
    onChange(list);
  };

  // 1) user-scoped
  {
    const q1 = query(
      collection(db, `users/${uid}/notifications`),
      orderBy("timestamp", "desc"),
      limit(pageSize)
    );
    unsubs.push(
      onSnapshot(
        q1,
        (snap) => {
          snap.docChanges().forEach((c) => (c.type === "removed" ? pop(c.doc) : push(c.doc)));
          emit();
        },
        (err) => console.warn("[Inbox q1]", err?.code || err)
      )
    );
  }

  // 2) collection-group user-kind
  {
    const q2 = query(
      collectionGroup(db, "notifications"),
      where("kind", "==", "user"),
      where("userId", "==", uid),
      orderBy("timestamp", "desc"),
      limit(pageSize)
    );
    unsubs.push(
      onSnapshot(
        q2,
        (snap) => {
          snap.docChanges().forEach((c) => (c.type === "removed" ? pop(c.doc) : push(c.doc)));
          emit();
        },
        (err) => console.warn("[Inbox q2]", err?.code || err)
      )
    );
  }

  // 3) team notifications
  if (teamIds.length) {
    const groups = chunk(teamIds, 10);
    groups.forEach((ids) => {
      const q3 = query(
        collectionGroup(db, "notifications"),
        where("kind", "==", "team"),
        where("teamId", "in", ids),
        orderBy("timestamp", "desc"),
        limit(pageSize)
      );
      unsubs.push(
        onSnapshot(
          q3,
          (snap) => {
            snap.docChanges().forEach((c) => (c.type === "removed" ? pop(c.doc) : push(c.doc)));
            emit();
          },
          (err) => console.warn("[Inbox q3]", err?.code || err)
        )
      );
    });
  }

  return () => unsubs.forEach((u) => u());
}

export async function markNotificationRead(path, isRead = true) {
  await updateDoc(doc(db, path), {
    isRead,
    readAt: isRead ? serverTimestamp() : null,
  });
}

export async function deleteNotification(path) {
  await deleteDoc(doc(db, path));
}

function chunk(arr, size) {
  const out = [];
  for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
  return out;
}

/**
 * Team-scoped notifier used by team and match flows.
 */
export async function sendFCMToTeam({ teamId, title, body = "", data = {} }) {
  if (!teamId) throw new Error("teamId is required");

  const cleanData = data || {};
  const copy = premiumizeCopy({ title, body, data: cleanData });

  // 1) Local UI toast event
  try {
    window.dispatchEvent(
      new CustomEvent("scrimly:push", {
        detail: {
          notification: { title: copy.title || "Scrimly", body: copy.body || "" },
          data: { ...(cleanData || {}), type: cleanData?.type || "generic", teamId },
        },
      })
    );
  } catch {}

  // 2) Persist under team
  try {
    await addDoc(collection(db, `teams/${teamId}/notifications`), {
      kind: "team",
      teamId,
      title: String(copy.title || "Scrimly"),
      body: String(copy.body || ""),
      data: cleanData,
      isRead: false,
      createdAt: serverTimestamp(),
      timestamp: serverTimestamp(),
    });
  } catch (e) {
    console.warn("[sendFCMToTeam] write skipped/failed:", e?.message || e);
  }
}

export default {
  sendNotificationToUser,
  sendFCMToTeam,
  subscribeToInbox,
  markNotificationRead,
  deleteNotification,
};
